import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DoCheck,
  effect,
  inject,
  input,
  OnInit,
  output,
  Signal,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { HxBaseControl } from '../../base/hx-base-control';
import { createIdSignal } from '../../base/id-manager';
import { HxControlSize } from '../../base/index';
import { validateRequired } from '../../base/validation-helpers';
import { HxButton } from '../button/button';
import { HxIcon } from '../icon/icon';
import { HxLabel } from '../label/label';
import { HxTagComponent } from '../tag/tag';

export type RatingSize = Extract<HxControlSize, 'xs' | 'sm' | 'md' | 'lg' | 'xl'>;

@Component({
  selector: 'hx-rating',
  standalone: true,
  imports: [CommonModule, HxIcon, HxButton, HxLabel, HxTagComponent],
  templateUrl: './rating.html',
  styleUrls: ['./rating.css', '../../styles/form-control-tokens.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class HxRating extends HxBaseControl implements OnInit, DoCheck {
  private readonly cdr = inject(ChangeDetectorRef);
  private _previousExternalTouched = false;

  // ============================================================
  // INPUT SIGNALS
  // ============================================================

  readonly idInput = input<string | undefined>(undefined);
  readonly computedId = createIdSignal(this.idInput, 'hx-rating-');
  readonly customCssClass = input<string>('');
  readonly type = input<'numerical' | 'star'>('numerical');
  readonly ICON = 'star';
  readonly size = input<RatingSize>('md');
  readonly scaleCount = input<number>(5);
  readonly columns = signal(1);
  readonly justification = input<'left' | 'centre' | 'right'>('left');
  readonly questionLabel = input<string>('');
  readonly subtitle = input<string>('');
  readonly helpText = input<string>('');
  readonly minLabel = input<string>('');
  readonly maxLabel = input<string>('');
  readonly columnLabels = input<string[]>([]);
  readonly showSlider = input<boolean>(true);
  readonly startFromZero = input<boolean>(false);
  readonly requiredErrorMessage = input<string>('');
  readonly enableCodeMapping = input<boolean>(false);
  readonly codeMappings = input<
    | Record<
        string | number,
        Array<{ code: string; display?: string; description?: string; type?: string; [key: string]: unknown }>
      >
    | undefined
  >(undefined);

  // ============================================================
  // OUTPUT EVENTS
  // ============================================================

  readonly selectionChange = output<number | null>();
  readonly codeRemoved = output<{ item: number; code: string }>();
  readonly touched = output<void>();
  readonly blurred = output<void>();

  // ============================================================
  // INTERNAL STATE
  // ============================================================

  private readonly _selectedValue = signal<number | null>(null);
  readonly hoveredValue = signal<number | null>(null);
  private readonly isFocused = signal<boolean>(false);

  protected readonly validationError = signal<{ isInvalid: boolean; message: string }>({
    isInvalid: false,
    message: '',
  });

  private readonly previousValidScaleCount = signal<number>(5);

  constructor() {
    super();
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
    effect(() => {
      this.syncValidConfig();
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();
    const externalControl = this.ngControl?.control;
    if (externalControl) {
      externalControl.statusChanges?.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
        this.updateValidationState();
      });
    }
    this.updateValidationState();
  }

  ngDoCheck(): void {
    const externalControl = this.ngControl?.control;
    if (externalControl) {
      const currentTouched = externalControl.touched;
      if (currentTouched !== this._previousExternalTouched) {
        this._previousExternalTouched = currentTouched;
        this.updateValidationState();
      }
    }
  }

  // ============================================================
  // COMPUTED SIGNALS
  // ============================================================

  get selectedValue(): Signal<number | null> {
    return this._selectedValue.asReadonly();
  }

  protected readonly ratingItems = computed(() => {
    const count = this.getValidScaleCount();
    const start = this.startFromZero() ? 0 : 1;
    const length = this.startFromZero() ? count + 1 : count;
    return Array.from({ length }, (_, i) => i + start);
  });

  protected readonly firstFocusableItem = computed(() => {
    return this.startFromZero() ? 0 : 1;
  });

  protected readonly maxRatingValue = computed(() => {
    const items = this.ratingItems();
    return items[items.length - 1] ?? this.getValidScaleCount();
  });

  protected readonly processedLabels = computed(() => {
    return {
      question: this.questionLabel(),
      subtitle: this.subtitle(),
      help: this.helpText(),
      min: this.minLabel(),
      max: this.maxLabel(),
    };
  });

  protected readonly sliderProgress = computed(() => {
    const val = this._selectedValue();
    const min = this.startFromZero() ? 0 : 1;
    const max = this.maxRatingValue();

    if (val === null || val < min) {
      return '0%';
    }
    if (max === min) {
      return '100%';
    }
    const percent = ((val - min) / (max - min)) * 100;
    return `${percent}%`;
  });

  protected readonly ariaDescribedBy = computed(() => {
    const ids: string[] = [];
    const ratingId = this.computedId();

    if (this.processedLabels().help) {
      ids.push(`help-${ratingId}`);
    }

    if (this.validationError().isInvalid) {
      ids.push(`error-${ratingId}`);
    }

    return ids.length > 0 ? ids.join(' ') : null;
  });

  readonly hasVisibleCodes = computed(() => {
    if (!this.enableCodeMapping()) return false;
    const mappings = this.codeMappings();
    if (!mappings || typeof mappings !== 'object') return false;
    return this.ratingItems().some((item) => this.getItemCodes(item).length > 0);
  });

  protected readonly hostCssClasses = computed(() => {
    const classes = ['hx-rating', this.cssClass(), this.customCssClass()];
    classes.push(`hx-rating--${this.justification()}`);
    classes.push(`hx-rating--${this.type()}`);
    classes.push(`hx-rating--size-${this.size()}`);

    if (this.hasVisibleCodes()) {
      classes.push('hx-rating--has-codes');
    }
    if (this.disabledState()) {
      classes.push('hx-rating--disabled');
    }
    if (this.validationError().isInvalid) {
      classes.push('hx-rating--invalid');
    }
    if (this.isFocused()) {
      classes.push('hx-rating--focused');
    }
    if (this.readonly()) {
      classes.push('hx-rating--readonly');
    }

    return classes.filter(Boolean).join(' ');
  });

  protected readonly itemCssClasses = computed(() => {
    const map = new Map<number, string>();
    const selected = this._selectedValue();
    const hovered = this.hoveredValue();
    const isNumerical = this.type() === 'numerical';

    for (const item of this.ratingItems()) {
      const classes = ['hx-rating__item'];

      if (selected !== null) {
        const isSelected = isNumerical ? item === selected : item <= selected;

        if (isSelected) {
          classes.push('hx-rating__item--selected');
        }
      }

      if (hovered !== null) {
        const isHovered = isNumerical ? item === hovered : item <= hovered;

        if (isHovered) {
          classes.push('hx-rating__item--hovered');
        }
      }

      map.set(item, classes.join(' '));
    }
    return map;
  });

  // ============================================================
  // BASE CONTROL OVERRIDES
  // ============================================================

  protected override _setValue(value: number | null | undefined): void {
    if (value === undefined) {
      value = null;
    }
    this._selectedValue.set(value);
    this.updateValidationState();
  }

  protected override _validate(_control: AbstractControl): ValidationErrors | null {
    return validateRequired(this.required(), this._selectedValue() !== null);
  }

  override setDisabledState(isDisabled: boolean): void {
    super.setDisabledState(isDisabled);
  }

  // ============================================================
  // PUBLIC ACTIONS
  // ============================================================

  selectRating(value: number): void {
    if (this.disabledState()) {
      return;
    }

    // Toggle selection: if clicking the same rating, deselect it
    const newValue = this._selectedValue() === value ? null : value;
    this._selectedValue.set(newValue);
    this.selectionChange.emit(newValue);
    this.notifyChange(newValue);

    if (!this.ngControl?.touched) {
      this.markTouched();
    }

    this.updateValidationState();
  }

  onSliderInput(event: Event): void {
    if (this.disabledState()) {
      return;
    }
    const target = event.target as HTMLInputElement;
    const val = parseInt(target.value, 10);
    if (!isNaN(val)) {
      this.selectRating(val);
    }
  }

  onBlur(): void {
    this.isFocused.set(false);
    this.hoveredValue.set(null);
    this.blurred.emit();
    this.markTouched();
    this.updateValidationState();
  }

  onFocus(): void {
    if (this.disabledState()) {
      return;
    }
    this.isFocused.set(true);
    this.touched.emit();
  }

  onItemHover(item: number): void {
    if (this.disabledState()) {
      return;
    }
    this.hoveredValue.set(item);
  }

  onItemLeave(): void {
    this.hoveredValue.set(null);
  }

  onKeyDown(event: KeyboardEvent, item?: number): void {
    if (this.disabledState()) {
      return;
    }

    const min = this.startFromZero() ? 0 : 1;
    const current = this._selectedValue() ?? min - 1;
    const max = this.maxRatingValue();
    let handled = false;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        const prev = Math.max(min, current - 1);
        this.selectRating(prev);
        handled = true;
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        const next = Math.min(max, current + 1);
        this.selectRating(next);
        handled = true;
        break;
      case ' ':
      case 'Enter':
        if (item) {
          this.selectRating(item);
        }
        handled = true;
        break;
    }

    if (handled) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  // ============================================================
  // HELPER METHODS
  // ============================================================

  getRatingItemContent(item: number): string {
    return this.type() === 'numerical' ? item.toString() : '';
  }

  getItemLabel(item: number): string | null {
    const labels = this.columnLabels();
    const index = this.startFromZero() ? item : item - 1;
    return labels[index] || null;
  }

  isActive(item: number): boolean {
    const selected = this.selectedValue();
    const hovered = this.hoveredValue();

    if (this.type() === 'numerical') {
      const isSelected = selected !== null && item === selected;
      const isHovered = hovered !== null && item === hovered;
      return isSelected || isHovered;
    }

    const selVal = selected ?? 0;
    const hovVal = hovered ?? 0;
    return item <= selVal || item <= hovVal;
  }

  getColumnIndices(): number[] {
    return Array.from({ length: this.getValidColumns() }, (_, i) => i);
  }

  getItemsForColumn(): number[] {
    return this.ratingItems();
  }

  // ============================================================
  // PRIVATE LOGIC
  // ============================================================

  getValidColumns(): number {
    return this.columns();
  }

  private getValidScaleCount(): number {
    const count = this.scaleCount();
    if (count >= 2 && count <= 10 && Number.isInteger(count)) {
      return count;
    }
    return this.previousValidScaleCount();
  }

  private updateValidationState(): void {
    const formControl = this.ngControl?.control;
    const isTouched = formControl?.touched ?? this.ngControl?.touched ?? false;
    const hasRequiredError = formControl
      ? formControl.hasError('required')
      : this.required() && this._selectedValue() === null;
    const isInvalid = isTouched && hasRequiredError;

    let errorMessage = '';
    if (isInvalid) {
      const customMessage = this.requiredErrorMessage();
      // Use custom message if provided and not empty
      if (customMessage && customMessage.trim().length > 0) {
        errorMessage = customMessage;
      } else if (this.processedLabels().question) {
        // Fall back to label-based message
        errorMessage = `Please rate: ${this.processedLabels().question}`;
      } else {
        // Final fallback
        errorMessage = 'This field is required';
      }
    }

    this.validationError.set({
      isInvalid,
      message: errorMessage,
    });
  }

  private syncValidConfig(): void {
    const count = this.scaleCount();
    if (count >= 2 && count <= 10 && Number.isInteger(count)) {
      this.previousValidScaleCount.set(count);
    }
  }

  /** Return the medical codes mapped to the given rating item number */
  getItemCodes(
    item: number
  ): Array<{ code: string; display?: string; description?: string; type?: string; [key: string]: unknown }> {
    const mappings = this.codeMappings();
    if (!mappings) return [];

    const start = this.startFromZero() ? 0 : 1;
    const result: Array<{
      code: string;
      display?: string;
      description?: string;
      type?: string;
      [key: string]: unknown;
    }> = [];
    const seenCodes = new Set<string>();

    for (const [key, codes] of Object.entries(mappings)) {
      if (!Array.isArray(codes)) continue;

      let min: number;
      let max: number;

      if (key.includes('-')) {
        const parts = key.split('-').map((p) => Number(p.trim()));
        const p0 = !isNaN(parts[0]) ? parts[0] : start;
        const p1 = parts.length > 1 && !isNaN(parts[1]) ? parts[1] : p0;
        min = Math.min(p0, p1);
        max = Math.max(p0, p1);
      } else {
        const num = Number(key.trim());
        if (isNaN(num)) continue;
        min = num;
        max = num;
      }

      if (item >= min && item <= max) {
        for (const codeItem of codes) {
          if (codeItem?.code && !seenCodes.has(codeItem.code)) {
            seenCodes.add(codeItem.code);
            result.push(codeItem);
          }
        }
      }
    }

    return result;
  }

  /** Emit codeRemoved output when a badge remove button is clicked */
  onRemoveCode(item: number, code: string): void {
    this.codeRemoved.emit({ item, code });
  }

  /** Return the code type (e.g. 'ICD', 'CPT', 'CPT2') for display as a badge on the tag */
  getCodeType(codeItem: { code?: string; type?: string; [key: string]: unknown }): string {
    if (codeItem?.type) {
      return String(codeItem.type).toUpperCase();
    }
    const code = codeItem?.code || '';
    if (/^[A-Z]\d{2}/i.test(code)) {
      return 'ICD';
    }
    if (/^\d{4,5}[A-Z]?$/i.test(code)) {
      return 'CPT';
    }
    return '';
  }
}
